// ตัวอย่าง response ที่ไม่ใช่ 200 ของแต่ละ endpoint ในหน้า /document-api (แสดงเป็น tab ใต้หัวข้อ Response)
// อ้างอิงจากโค้ด PaymentBroker-ApiPartner (helper BadRequest / InternalError, middleware/authorization.js และ service ของแต่ละ route)
// ถ้าเพิ่ม/เปลี่ยน error code ฝั่ง ApiPartner ให้อัปเดตไฟล์นี้ด้วย
//
// รูปแบบ error ทุกตัว: { status: { code, message }, data: null }  (KYC มี data เพิ่ม)

// ชื่อ tab = code + ข้อความสั้น (code เดียวกันอย่าง D-4000 มีหลายแบบ ต้องแยกให้ออก)
const shortLabel = (code, message) => {
  const text = String(message).replace(/\s*[:(].*$/, "").trim();
  return `${code} · ${text.length > 28 ? `${text.slice(0, 26).trim()}…` : text}`;
};

// group: ใช้จัดแถวของ tab (Validation / Business / Authentication / Server) ไม่ระบุ = เดาจาก code
const groupOf = (code) => (code === "D-4000" ? "Validation" : "Business");

const error = (http, code, message, when, data = null, group = groupOf(code)) => ({
  http,
  group,
  label: shortLabel(code, message),
  when,
  body: { status: { code, message }, data },
});

// guard ของทุก endpoint ที่เซ็น request (client keys + x-signature + Bearer token + certificate/IP)
const auth = (http, code, message, when) => error(http, code, message, when, null, "Authentication");

const SIGNED_COMMON = [
  auth(400, "P-4013", "Token has expired", "The access token from Auth has expired (tokens last 30 minutes) or is invalid. Request a new token."),
  auth(401, "P-4017", "Invalid signature format", "x-signature does not match HMAC-SHA256(client_secret, client_id + x-timestamp + request body)."),
  auth(400, "P-4018", "Invalid or expired timestamp in the request", "x-timestamp is more than 30 seconds away from server time."),
  auth(400, "C-4002", "The IP address is not in the whitelist", "The calling IP is not registered for this broker."),
];

const INTERNAL = error(500, "IN-5000", "Internal Server Error", "Unexpected server error. Retry later or contact support with your ref_no.", null, "Server");

const ENDPOINT_ERRORS = {
  "check health": [],
  auth: [
    auth(400, "P-4014", "Missing required header key", "One of client_id, client_secret, client_ref_id, x-apigw-api-id, x-timestamp, x-signature is missing."),
    auth(400, "P-4015", "Invalid format for required header key", "A header value does not match its expected format."),
    auth(401, "P-4017", "Invalid signature format", "x-signature does not match HMAC-SHA256(client_secret, client_id + x-timestamp + request body)."),
    auth(400, "P-4018", "Invalid or expired timestamp in the request", "x-timestamp is more than 30 seconds away from server time."),
    auth(400, "P-4003", "Data not found", "The client key set does not match any broker."),
    auth(429, "P-4029", "Too many requests. Next allowed at 3:45:12 PM", "Token requests exceeded the rate limit for this IP."),
  ],
  "get transactions": [
    error(400, "D-4000", "Start date is required", "start_date / end_date are required when ref_no is not sent."),
    error(400, "D-4000", "Invalid date format, expected YYYY-MM-DD", "start_date or end_date is not in YYYY-MM-DD format."),
    error(400, "D-4000", "Limit transaction is 30 days", "start_date is more than 30 days ago."),
  ],
  "get balance": [],
  "get bank": [error(400, "P-4003", "Bank data not found", "The bank uid does not exist or is inactive.")],
  "get customer": [error(400, "P-4003", "Customer data not found", "No customer matches the given uid or customer_ref.")],
  "create customer": [
    error(400, "D-4000", "Missing required field : currency", "A required field is missing (currency, name_th, name_en, bank_code, bank_account_name_th, bank_account_name_en, bank_account_number)."),
    error(400, "D-4000", "The field 'bank_account_number' must be a numeric [0-9] value", "bank_account_number contains non-digit characters."),
    error(400, "P-4007", "Bank 'BCEL' does not support currency 'THB'", "bank_code does not exist or is not available for the currency."),
    error(400, "P-4031", "Customer account is blacklisted", "The bank account is on the blacklist."),
    error(200, "P-4004", "This bank account already exists", "The bank account is already registered. Returned with HTTP 200 and the existing customer in data, including its current kyc_status and kyc_url."),
  ],
  "update customer": [
    error(400, "P-4003", "Customer data not found", "The customer uid does not exist or belongs to another broker."),
    error(400, "P-4004", "This bank account already exists", "The new bank account is already used by another customer."),
    error(400, "P-4007", "Bank data not found", "bank_code does not exist or is not available for the customer's currency."),
    error(400, "P-4031", "Customer account is blacklisted", "The customer or the new bank account is blacklisted."),
  ],
  "update customer batch": [
    error(400, "D-4000", "Missing required field : customer_lists", "customer_lists is missing or empty."),
    error(400, "P-4008", "Duplicate bank accounts found in request: 1234567890", "The same bank account appears more than once in customer_lists."),
    error(400, "P-4031", "Customer account is blacklisted", "One of the customers or bank accounts is blacklisted."),
    error(400, "P-4007", "Bank data not found", "bank_code does not exist or is not available for the customer's currency."),
  ],
  "verify account": [
    auth(401, "P-4011", "Invalid authentication format", "The Bearer token is missing, malformed or not valid."),
    error(400, "D-4000", "Only one of the fields 'bank_account', 'document_id', or 'full_name' must be provided", "The body contains more than one search field."),
    error(400, "D-4000", "The field bank_account must be an array", "bank_account is not an array, is empty or has more than 100 items."),
    error(400, "P-4022", "Something went wrong", "The verification service returned an unexpected result. Retry later."),
  ],
  "deposits thai": [
    error(400, "D-4000", "Missing required field : customer_id", "customer_id, amount or ref_no is missing."),
    error(400, "D-4000", "Amount must be a numeric value with up to 2 decimal places", "amount is not a number or has more than 2 decimals."),
    error(400, "P-4003", "Customer data not found", "customer_id does not exist for this broker."),
    error(400, "P-4031", "Customer account is blacklisted", "The customer is on the blacklist."),
    error(
      400,
      "P-4034",
      "Customer KYC required",
      "The customer has not passed identity verification. Send the customer to kyc_url, then retry. kyc_status: none | pending | review | rejected | expired | bank_required.",
      { kyc_status: "none", kyc_url: "https://swiftgoldpay.com/kyc?t=eyJyZWYiOi..." },
    ),
    error(400, "P-4004", "This reference number already exists", "ref_no was already used for another transaction."),
    error(400, "P-4008", "Minimum amount is 100 THB", "amount is below the minimum deposit for this gateway."),
    error(400, "P-4008", "Maximum amount is 50000 THB", "amount is above the maximum deposit for this gateway."),
    error(400, "P-4008", "Amount is below the per-transaction minimum of 500 THB", "amount is below the per-transaction minimum set for your account."),
    error(400, "P-4008", "Amount exceeds the per-transaction limit of 100,000 THB", "amount is above the per-transaction limit set for your account."),
    error(400, "P-4033", "Deposit limit reached for today. Please try again tomorrow", "The daily deposit volume limit is full. The monthly limit returns \"Deposit limit reached for this month. Please try again next month\"."),
    error(400, "P-4022", "Transaction failed", "The payment gateway could not create the QR."),
  ],
  "withdrawals thai": [
    error(400, "D-4000", "Missing required field : customer_id", "customer_id, amount or ref_no is missing."),
    error(400, "P-4003", "Customer data not found", "customer_id does not exist for this broker, or the customer is not a THB customer."),
    error(400, "P-4031", "Customer account is blacklisted", "The customer is on the blacklist."),
    error(
      400,
      "P-4034",
      "Customer KYC required",
      "The customer has not passed identity verification. Send the customer to kyc_url, then retry.",
      { kyc_status: "none", kyc_url: "https://swiftgoldpay.com/kyc?t=eyJyZWYiOi..." },
    ),
    error(400, "P-4008", "Minimum amount is 100 THB", "amount is below the minimum or above the maximum withdrawal for this gateway."),
    error(400, "P-4019", "Insufficient balance in the wallet", "The broker wallet does not have enough balance (including the minimum balance)."),
    error(400, "P-4004", "This reference number already exists", "ref_no was already used for another transaction."),
    error(400, "P-4032", "Gateway timeout, please retry.", "The payment gateway did not answer within 60 seconds."),
    error(400, "P-4022", "Transaction failed", "The payment gateway rejected the payout. The amount was returned to the wallet."),
  ],
  "settlement hq thai": [
    error(400, "P-4008", "Minimum amount is 300 THB", "amount is below the settlement minimum."),
    error(400, "P-4019", "Insufficient balance in the wallet", "The broker wallet does not have enough balance."),
    error(400, "P-4004", "This reference number already exists", "ref_no was already used for another transaction."),
  ],
  "refund mismatch": [
    error(400, "P-4005", "Transaction data not found", "ref_no is not a THB deposit with status mismatch (use Refund Mismatch Laos for LAK)."),
    error(400, "P-4004", "This transaction has already been refunded", "A refund for this ref_no was already made."),
    error(400, "P-4008", "Amount cannot exceed 500.00 THB", "amount is more than the deposited amount."),
    error(400, "P-4019", "Insufficient balance in the wallet", "The broker wallet does not have enough balance for the refund."),
  ],
  "deposits laos": [
    error(400, "D-4000", "Missing required field : customer_id", "customer_id, amount or ref_no is missing."),
    error(400, "D-4000", "Amount must be a numeric value with up to 2 decimal places and end with .00 only", "LAK amounts must be whole numbers (.00). A non-numeric amount returns \"Amount must be a numeric value with up to 2 decimal places\"."),
    error(400, "P-4003", "Customer data not found", "customer_id does not exist for this broker."),
    error(400, "P-4004", "This reference number already exists", "ref_no was already used for another transaction."),
    error(400, "P-4008", "Minimum amount is 30000 LAK", "amount is below the minimum or above the maximum."),
    error(400, "P-4022", "Transaction failed", "The payment gateway could not create the QR."),
  ],
  "withdrawals laos": [
    error(400, "D-4000", "Amount must be a numeric value with up to 2 decimal places and end with .00 only", "LAK amounts must be whole numbers (.00)."),
    error(400, "P-4003", "Customer data not found", "customer_id does not exist for this broker, is not a LAK customer, or is blacklisted."),
    error(400, "P-4033", "Daily limit exceeded for this account", "The destination account reached its daily withdrawal limit."),
    error(400, "P-4019", "Insufficient balance in the wallet", "The broker wallet does not have enough balance (including the minimum balance)."),
    error(400, "P-4022", "Transaction failed", "The payment gateway rejected the payout."),
  ],
  "settlement hq laos": [
    error(400, "P-4008", "Minimum amount is 30000 LAK", "amount is below the settlement minimum."),
    error(400, "P-4019", "Insufficient balance in the wallet", "The broker wallet does not have enough balance."),
    error(400, "P-4004", "This reference number already exists", "ref_no was already used for another transaction."),
  ],
  "check bank account laos": [
    error(400, "D-4000", "Missing required field : bank_code", "bank_code or bank_account_number is missing."),
    error(400, "P-4007", "Bank data not found", "bank_code does not exist."),
    error(400, "P-4022", "Bank account verification failed", "The bank could not verify this account number."),
  ],
  "refund mismatch laos": [
    error(400, "P-4005", "Transaction data not found", "ref_no is not a LAK deposit with status mismatch."),
    error(400, "P-4004", "This transaction has already been refunded", "A refund for this ref_no was already made."),
    error(400, "P-4008", "Amount cannot exceed 500000.00 LAK", "amount is more than the deposited amount."),
    error(400, "P-4019", "Insufficient balance in the wallet", "The broker wallet does not have enough balance for the refund."),
  ],
};

// endpoint ที่ไม่ผ่าน guard เซ็น request (health / verify account ใช้ public token / auth มี error ของตัวเองครบแล้ว)
const UNSIGNED = new Set(["check health", "auth", "verify account"]);

/**
 * tab ทั้งหมดของ endpoint: Success (จาก response ใน Postman collection) + error ของ endpoint + error ร่วม
 */
export function getApiResponses(item) {
  const name = String(item?.name || "").toLowerCase();
  const success = { http: 200, group: "Success", label: "Success", when: null, body: item?.response ?? null };
  const own = ENDPOINT_ERRORS[name] || [];
  const common = UNSIGNED.has(name) || name === "check health" ? [] : SIGNED_COMMON;
  const tail = name === "check health" ? [] : [INTERNAL];
  return [success, ...own, ...common, ...tail];
}

const GROUP_ORDER = ["Success", "Validation", "Business", "Authentication", "Server"];

/** จัด tab เป็นกลุ่มตาม GROUP_ORDER คืน [{ group, items: [{ index, ...response }] }] (index = ตำแหน่งใน getApiResponses) */
export function groupApiResponses(responses) {
  return GROUP_ORDER.map((group) => ({
    group,
    items: responses.map((r, index) => ({ ...r, index })).filter((r) => r.group === group),
  })).filter((g) => g.items.length);
}
